import { useState } from 'react';
import { Violet300 } from '@zep-us/zus/tokens';
import { debounce } from 'es-toolkit';
import { useTranslation } from 'next-i18next';
import { Textarea } from '@/components/ui/Textarea';
import { useEffectOnce } from '@/hooks';
import { PenFillIcon } from '@/icons';
import { LoadingSpinner } from '@/shared/components';
import { DialogFooter } from '@/ui_v3';
import { cn } from '@/utils';
import { GenerateAction } from '@/views/build/modal/BuildAiModal/components/BuildAiGenerateAction';
export const SimilarQuizPrompt = ({
  onGenerate,
  onMount,
  onCancel,
  previousPrompt,
  promptPresets
}: {
  onGenerate: (prompt: string, keywordInfo?: {
    keyword: string;
    keywordIndex: number;
  }) => void;
  onMount: () => void;
  onCancel: () => void;
  previousPrompt?: string;
  promptPresets: string[];
}) => {
  const {
    t
  } = useTranslation(['build']);
  const [promptInput, setPromptInput] = useState<string>(previousPrompt ?? '');
  return <>
      <div className="grow overflow-y-scroll bg-gray-50 text-body-md-regular" onMouseDown={e => e.stopPropagation()}>
        <div className="relative flex flex-col justify-center p-lg">
          <div className="flex w-full flex-col justify-start gap-xl">
            <div className="flex w-full flex-col items-center justify-start gap-md pt-[70px]">
              <div className="flex w-full flex-col items-center gap-xs">
                <img src="/assets/ai_face.png" width={40} alt="airobot" />
                <h4 className="text-center text-heading-lg-extrabold text-default">
                  {t('build:similarQuiz.modal.promptDescription')}
                </h4>
              </div>
              <section className="flex w-full flex-col items-center justify-start gap-xl">
                <div className={cn('bg-[length:calc(100%+4px)_100%] bg-center', 'h-[100px] shadow-xs rounded-md overflow-hidden focus-within:shadow-lg relative w-full max-w-[570px]', 'border-2 border-transparent bg-gradient-to-r from-purple-400/50 to-blue-400/50', 'focus-within:from-purple-400 focus-within:to-blue-400')}>
                  <Textarea onMouseDown={e => e.stopPropagation()} value={promptInput} onChange={e => setPromptInput(e.target.value)} placeholder={t('build:similarQuiz.modal.promptPlaceholder')} className="size-full rounded-none outline-0 outline-transparent focus:outline-0 focus:outline-transparent" data-sentry-element="Textarea" data-sentry-source-file="SimilarQuizPrompt.tsx" />
                </div>
              </section>
            </div>
            <PromptPresets onMount={onMount} onPresetClick={(preset, index) => onGenerate(preset, {
            keyword: preset,
            keywordIndex: index + 1
          })} presets={promptPresets} data-sentry-element="PromptPresets" data-sentry-source-file="SimilarQuizPrompt.tsx" />
          </div>
        </div>
      </div>

      <DialogFooter onMouseDown={e => e.stopPropagation()} className="bg-default shadow-reversed-sm mobile-lg:bg-gray-50 mobile-lg:shadow-none" data-sentry-element="DialogFooter" data-sentry-source-file="SimilarQuizPrompt.tsx">
        <PromptAction onConfirm={() => onGenerate(promptInput)} onCancel={onCancel} data-sentry-element="PromptAction" data-sentry-source-file="SimilarQuizPrompt.tsx" />
      </DialogFooter>
    </>;
};
const PromptPresets = ({
  presets,
  onPresetClick,
  onMount
}: {
  presets: string[];
  onPresetClick: (preset: string, index: number) => void;
  onMount: () => void;
}) => {
  //중복 호출을 방지하기 위한 디바운싱
  const debouncedOnMount = debounce(onMount, 200);
  useEffectOnce(() => {
    debouncedOnMount();
    return () => {
      debouncedOnMount.cancel();
    };
  });
  return <div className="flex w-full flex-col items-center gap-sm pb-lg" data-sentry-component="PromptPresets" data-sentry-source-file="SimilarQuizPrompt.tsx">
      {presets.length === 0 && <LoadingSpinner className="size-[40px] text-primary" />}
      {presets.map((preset, index) => <PromptPresetChip style={{
      animationDelay: `${index * 500}ms`,
      animationDuration: `500ms`
    }} key={preset} preset={preset} onClick={() => {
      onPresetClick(preset, index);
    }} />)}
    </div>;
};
const PromptPresetChip = ({
  preset,
  onClick,
  style
}: {
  preset: string;
  onClick: () => void;
  style?: React.CSSProperties;
}) => {
  return <div style={style} className="flex animate-fade-up cursor-pointer items-center gap-2xs rounded-xl border border-[#E9EAF2] bg-white px-sm py-xs text-body-md-bold text-neutral shadow-xs hover:bg-neutral" onClick={onClick} data-sentry-component="PromptPresetChip" data-sentry-source-file="SimilarQuizPrompt.tsx">
      <PenFillIcon size={'2xs'} fill={Violet300} data-sentry-element="PenFillIcon" data-sentry-source-file="SimilarQuizPrompt.tsx" />
      {preset}
    </div>;
};
const PromptAction = ({
  onConfirm,
  onCancel
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return <GenerateAction onConfirm={onConfirm} onCancel={onCancel} isPending={false} className="bg-transparent tablet-sm:p-0" data-sentry-element="GenerateAction" data-sentry-component="PromptAction" data-sentry-source-file="SimilarQuizPrompt.tsx" />;
};